// src/api/adminCouponsApi.ts
import {http} from "./http";

export type CouponAppliesTo = "all" | "products" | "categories";
export type CouponDiscountType = "fixed" | "percentage";
export type CouponChannel = "online" | "pos" | "both";

export type AdminCoupon = {
  id: number;
  company_id: number | null;
  code: string;
  discount_type: CouponDiscountType;
  discount_value: string; // postgres numeric comes back as string often
  max_discount: string | null;
  channel: CouponChannel;
  applies_to: CouponAppliesTo;
  starts_at: string | null;
  ends_at: string | null;
  usage_limit: number | null;
  usage_per_user_limit: number | null;
  min_subtotal: string | null;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;

  total_used?: number;
  product_ids?: number[];
  category_ids?: number[];
};


export type AdminCouponUsageRow = {
  id: number;
  coupon_id: number;
  coupon_code: string;
  customer_id: number | null;
  customer_email: string | null;
  order_id: number | null;
  discount_amount: number;
  used_at: string;
  ip_address: string | null;
  order_email?: string | null;
  order_customer_name?: string | null;
  order_number?: string | null;
};

export type AdminCouponUsageByEmail = {
  customer_email: string;
  total_uses: number;
  total_discount_amount: number;
  last_used_at: string;
  order_ids: number[];
};

export type AdminCouponUsageReport = {
  coupon: {
    id: number;
    code: string;
    is_active: boolean;
    usage_limit: number | null;
    usage_per_user_limit: number | null;
  };
  summary: {
    total_uses: number;
    unique_emails: number;
  };
  by_email: AdminCouponUsageByEmail[];
  usage_rows: AdminCouponUsageRow[];
};


export async function listAdminCoupons(params?: { active?: boolean; search?: string }) {
  const q = new URLSearchParams();
  if (params?.active === true) q.set("active", "true");
  if (params?.active === false) q.set("active", "false");
  if (params?.search) q.set("search", params.search);
  const url = `/api/admin/coupons${q.toString() ? `?${q.toString()}` : ""}`;
  const res = await http.get<AdminCoupon[]>(url);
  return res.data;
}

export async function getAdminCoupon(id: number) {
  const res = await http.get<AdminCoupon>(`/api/admin/coupons/${id}`);
  return res.data;
}

export async function createAdminCoupon(payload: Partial<AdminCoupon> & {
  code: string;
  discount_type: CouponDiscountType;
  discount_value: number;
  applies_to: CouponAppliesTo;
  product_ids?: number[];
  category_ids?: number[];
}) {
  const res = await http.post<AdminCoupon>(`/api/admin/coupons`, payload);
  return res.data;
}

export async function updateAdminCoupon(id: number, payload: any) {
  const res = await http.put<AdminCoupon>(`/api/admin/coupons/${id}`, payload);
  return res.data;
}

export async function toggleAdminCoupon(id: number, is_active: boolean) {
  const res = await http.patch<AdminCoupon>(`/api/admin/coupons/${id}/toggle`, { is_active });
  return res.data;
}

export async function deleteAdminCoupon(id: number) {
  const res = await http.delete<{ success: boolean }>(`/api/admin/coupons/${id}`);
  return res.data;
}

export const getAdminCouponUsageReport = async (
  id: number
): Promise<AdminCouponUsageReport> => {
  const res = await http.get(`/api/admin/coupons/${id}/usage`);
  return res.data;
};